import { Box, IconButton, Stack, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LandingSectionTitle } from '../landing-section-title';

import { copyIcon } from '../../config/icons';
import { copyToClipboard } from '../../utils';

import { MapTypeToStyle } from '../mapbox-common/config';
import { Minimap } from '../mapbox-common/minimap';
import { Layer, MapType, PlaceProps } from '../mapbox-common/types';
import { PlayOverlay } from '../play-overlay';

import Center from './components/center';
import CurrentPlaceCard from './components/current-place-card';
import CurrentRoute from './components/current-route';
import LayersSwitch from './components/layers-switch';
import Places from './components/places';
import { useMapboxStore, useMapboxStoreActions } from './store';

import 'mapbox-gl/dist/mapbox-gl.css';
import { Map, MapProvider, useMap } from 'react-map-gl/mapbox';

// @ts-ignore
import mbClient from '@mapbox/mapbox-sdk';
// @ts-ignore
import mbDirections from '@mapbox/mapbox-sdk/services/directions';

// save Maps API refs to store from context values
const InitMapStore = ({ mapboxToken, mapId }: { mapboxToken: string; mapId: string }) => {
  const { [mapId]: mapRefCurrent } = useMap();
  const { mapRef } = useMapboxStore();
  const { setMapRef, setDirectionsClientRef, setCurrentPlaceId } = useMapboxStoreActions();

  const cleanUp = useCallback(() => {
    setCurrentPlaceId(undefined);
    setMapRef(undefined);
  }, []);

  useEffect(() => {
    if (!mapRefCurrent) return;
    if (mapRef) {
      cleanUp();
    }

    setMapRef(mapRefCurrent);
    const baseClient = mbClient({ accessToken: mapboxToken });
    const directionsClient = mbDirections(baseClient);
    setDirectionsClientRef(directionsClient);

    // remove unwanted labels
    const map = mapRefCurrent.getMap();
    map.on('style.load', () => {
      if (!!map.getLayer('poi-label')) {
        map.setLayoutProperty('poi-label', 'visibility', 'none');
      }
    });

    return () => {
      cleanUp();
    };
  }, [mapRefCurrent]);

  return <></>;
};

export type MapboxProps = {
  lang: string;
  center: { logo: string; location: { lat: number; lng: number } };
  centerApi?: { lat: number; lng: number };
  address?: string;
  places?: PlaceProps[];
  initialMapType?: MapType;
  height?: string;
  displayLayers?: Layer[];
  minZoom?: number;
  maxZoom?: number;
  defaultZoom?: number;
  onLayersSwitch?: (mapType: MapType, layer: Layer) => void;
  t: (key: string) => string;
  mapboxToken: string;
  foursquareKey: string;
  mapId?: string;
};

export function Mapbox({
  center,
  centerApi = undefined,
  address,
  places = [], // custom places aligned with format
  initialMapType = MapType.Roadmap,
  height = '475px',
  displayLayers = [Layer.Educations, Layer.Shopping, Layer.Transport],
  minZoom = 5,
  maxZoom = 18,
  defaultZoom = 14,
  onLayersSwitch,
  t,
  mapboxToken = '',
  foursquareKey = '',
  lang = 'he',
  mapId = 'mapId', // should be present and unique on page
}: MapboxProps) {
  const { shadows } = useTheme();
  const { mapType } = useMapboxStore();
  const { setCenter, setCenterApi, setPlaces, setMapType, setDisplayLayers } =
    useMapboxStoreActions();
  const isLoading = useRef<boolean>(true);
  // const [scrollEnabled, setScrollEnabled] = useState(false);

  if (!center.location.lat || !center.location.lng) return null;

  useEffect(() => {
    isLoading.current = true;

    setCenter(center);
    if (centerApi) setCenterApi(centerApi);
    setPlaces(places);
    setMapType(initialMapType);
    setDisplayLayers(displayLayers);
    isLoading.current = false;
  }, [center]);

  if (isLoading.current) return null;
  return (
    <MapProvider>
      <InitMapStore mapboxToken={mapboxToken} mapId={mapId} />

      <Stack spacing="20px">
        {!!address && (
          <Stack direction="row" spacing={1}>
            <LandingSectionTitle title={address} />
            <IconButton
              onClick={() => {
                copyToClipboard(address);
              }}
              sx={{ width: '32px', height: '32px', padding: '0px', color: '#0494FE' }}
            >
              {copyIcon}
            </IconButton>
          </Stack>
        )}

        <LayersSwitch
          mapType={mapType}
          onLayersSwitch={onLayersSwitch}
          t={t}
          foursquareKey={foursquareKey}
          lang={lang}
        />

        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height,
            overflow: 'hidden',
            borderRadius: '12px',
            boxShadow: shadows[1],
          }}
        >
          <PlayOverlay label={t('labels.exploreMap')} sx={{ zIndex: 300 }} testKey="mapbox-play" />
          <Box sx={{ height: 'calc(100% + 25px)' }}>
            <Box
              sx={{
                position: 'absolute',
                zIndex: 200,
                insetInlineEnd: '30px',
                top: '30px',
              }}
            >
              <CurrentPlaceCard t={t} />
            </Box>

            <Box
              sx={{
                position: 'absolute',
                zIndex: 200,
                width: '82px',
                height: '82px',
                insetInlineStart: '16px',
                bottom: '16px',
              }}
            >
              <Minimap mapType={mapType} setMapType={setMapType} t={t} />
            </Box>

            <Map
              id={mapId}
              initialViewState={{
                latitude: center.location.lat,
                longitude: center.location.lng,
                zoom: defaultZoom,
              }}
              minZoom={minZoom}
              maxZoom={maxZoom}
              style={{ width: '100%', height: '100%' }}
              mapStyle={MapTypeToStyle[mapType]}
              mapboxAccessToken={mapboxToken}
              language={lang}
              // scrollZoom={false}
              // onClick={() => {setScrollEnabled(true)}}
            >
              <Center />
              <Places />
              <CurrentRoute />
            </Map>
          </Box>
        </Box>
      </Stack>
    </MapProvider>
  );
}
